// Shell: Topbar, Sidebar variants, router, global state
import React, { useState as useS, useEffect as useE, useMemo as useM, createContext as createC, useContext as useCtx } from 'react';
import { createPortal } from 'react-dom';
import { XPrim, XIcons } from './primitives.jsx';
import { XData, XEnv } from './data.jsx';
import { XApi } from './services.js';
import * as auth from './api/auth.js';
import { tokens as authTokens } from './api/tokens.js';
import { useT } from './i18n.jsx';
import { EmailVerifyModal } from './components/EmailVerifyModal.jsx';

// ---------- Route state ----------
// URL <-> in-memory state sync via History API. No router lib — kept tiny on purpose. Each
// page has a stable URL the user can bookmark/share/refresh. Browser back/forward works.
//
// To add a new page: insert one row into ROUTES below. Path params use `:name` syntax. Any
// extra fields beyond declared path params are serialized as `?key=value` query string.
const ROUTES = [
  { path: '/',                page: 'portal' },
  { path: '/market',          page: 'market' },
  { path: '/apis/:id',        page: 'api-detail' }, // /api is reserved for backend proxy (vite.config.js)
  { path: '/console',         page: 'console' },
  { path: '/earnings',        page: 'earnings' }, // 服务商收益 + 提现
  { path: '/mcp',             page: 'mcp' },
  { path: '/skill',           page: 'skill' },
  { path: '/skills',          page: 'skills' },
  { path: '/skills/console',  page: 'skills-console' },
  { path: '/skills/:slug',    page: 'skill-detail' },
  { path: '/workers', page: 'workers' },
  { path: '/workers/:id', page: 'workers' },
  { path: '/ai',              page: 'ai' },
  // Sandbox is the canonical Playground entry. Keep the old URLs as aliases so
  // bookmarks continue to work while the product exposes a single surface.
  { path: '/sandbox',         page: 'playground' },
  { path: '/sandbox-playground', page: 'playground' },
  { path: '/playground',      page: 'playground' },
  // Models browser lives under the AI 中转 namespace. /ai/models lists every
  // model we have pricing for; /ai/models/:slug drills into one model's providers.
  { path: '/ai/models',       page: 'models' },
  { path: '/ai/models/:slug', page: 'model-detail' },
  // AI spec system canonical model catalog (public docs).
  { path: '/ai/spec',         page: 'ai-spec' },
  { path: '/capabilities',    page: 'capability' },
  { path: '/capabilities/:id',page: 'capability' },
  { path: '/domains',         page: 'domains' },
  { path: '/workspace',       page: 'workspace' },
  { path: '/workspace',       page: 'my-apis' },          // legacy alias
  // ---- Single-API provider workspace (Phase 1 of redesign) ----
  // /workspace/new                              → 新建 API（暂复用 RegisterPage 的 new 模式）
  // /workspace/:apiId                           → 默认 basic 段
  // /workspace/:apiId/:section                  → basic | apis | tests | analytics | settings
  // /workspace/:apiId/analytics/:subsection     → traffic | users | revenue
  // /workspace/:apiId/endpoints/:endpointId     → D1 endpoint drill-down
  // 旧 /register?editId=xxx 由 #8 重定向到 /workspace/:apiId/basic。
  { path: '/workspace/new',                                  page: 'workspace-new' },
  { path: '/workspace/:apiId',                               page: 'workspace-api' },
  { path: '/workspace/:apiId/:section',                      page: 'workspace-api' },
  { path: '/workspace/:apiId/analytics/:subsection',         page: 'workspace-api' },
  { path: '/workspace/:apiId/endpoints/:endpointId',         page: 'workspace-endpoint' },
  { path: '/workspace/:apiId/callers/:callerId',             page: 'workspace-caller' },
  { path: '/register',        page: 'register' },
  { path: '/register/account',page: 'register-account' }, // legacy alias for /login
  { path: '/agent-import',    page: 'agent-import' },
  { path: '/admin',           page: 'admin' },
  // AI spec admin console — 方案 X 后只剩 canonical 目录 + pending 队列。
  // Provider 注册流程并入 marketplace /register。
  { path: '/admin/ai/pending',  page: 'admin-ai-pending' },
  { path: '/admin/ai/catalog',  page: 'admin-ai-catalog' },
  { path: '/login',           page: 'login' },
  { path: '/topup',           page: 'topup' },
  // Direct OAuth-bind page (replaces the old /claim wizard). One-click "Connect with X".
  { path: '/bind',            page: 'bind' },
  // Marketing activity — "tweet for $1 credit". Opt-in promo, not required for binding.
  { path: '/activity',        page: 'activity' },
  { path: '/docs',            page: 'docs' },
];

// Sandbox is still under local validation. Production keeps direct URLs usable
// for review, but does not advertise either legacy navigation entry yet.
const SHOW_SANDBOX_NAV = import.meta.env.DEV;

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Parse current location into { page, params }. Path params win over query params on key collision.
const parseUrl = (pathname, search) => {
  const sp = new URLSearchParams(search || '');
  const queryParams = {};
  sp.forEach((v, k) => { queryParams[k] = v; });
  // Most-specific match first (more :params or longer literal path).
  const sorted = [...ROUTES].sort((a, b) => {
    const aSpec = a.path.split('/').filter(Boolean).length;
    const bSpec = b.path.split('/').filter(Boolean).length;
    return bSpec - aSpec;
  });
  for (const r of sorted) {
    const segs = r.path.split('/').filter(Boolean);
    const re = '^/' + segs.map(s => s.startsWith(':') ? '([^/]+)' : escapeRe(s)).join('/') + '/?$';
    const m = pathname.match(new RegExp(re));
    if (!m) continue;
    const pathParams = {};
    // Capture groups are numbered by `:param` order, NOT by segment index — literal segments
    // don't create groups, so we need an independent counter.
    let groupIdx = 1;
    segs.forEach((s) => {
      if (s.startsWith(':')) {
        pathParams[s.slice(1)] = decodeURIComponent(m[groupIdx]);
        groupIdx++;
      }
    });
    return { page: r.page, params: { ...queryParams, ...pathParams } };
  }
  return { page: 'portal', params: queryParams };
};

// Build URL for a (page, params) pair. Picks the most-specific route definition for the page
// whose required path params are all present in `params`.
const buildUrl = (page, params = {}) => {
  const candidates = ROUTES.filter(r => r.page === page).sort((a, b) => {
    const aP = (a.path.match(/:/g) || []).length;
    const bP = (b.path.match(/:/g) || []).length;
    return bP - aP;
  });
  for (const r of candidates) {
    const required = (r.path.match(/:[a-zA-Z0-9_]+/g) || []).map(s => s.slice(1));
    if (required.every(p => params[p] != null && params[p] !== '')) {
      let path = r.path;
      const remaining = { ...params };
      required.forEach(p => { path = path.replace(':' + p, encodeURIComponent(params[p])); delete remaining[p]; });
      const qs = new URLSearchParams();
      Object.entries(remaining).forEach(([k, v]) => { if (v != null && v !== '' && !String(k).startsWith('_')) qs.set(k, String(v)); });
      const s = qs.toString();
      return path + (s ? '?' + s : '');
    }
  }
  // Unknown page (or required path param missing): fall back to ?path=foo so the bootstrap
  // shim still routes correctly on refresh.
  const qs = new URLSearchParams({ path: page });
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)); });
  return '/?' + qs.toString();
};

const RouteCtx = createC(null);
const useRoute = () => useCtx(RouteCtx);

// 旧链接重定向：/register[?editId=xxx] → 新工作区。
// 仅在 URL 命中旧路径时返回新 pathname+search，否则返回 null。
// 调用方负责 history.replaceState（保留 SPA 体验，不留旧 URL 在历史栈里）。
const rewriteLegacyUrl = (pathname, search) => {
  if (pathname === '/register' || pathname === '/register/') {
    const sp = new URLSearchParams(search || '');
    const editId = sp.get('editId');
    sp.delete('editId');
    const tail = sp.toString();
    if (editId) {
      return { pathname: `/workspace/${encodeURIComponent(editId)}/basic`, search: tail ? `?${tail}` : '' };
    }
    return { pathname: '/workspace/new', search: tail ? `?${tail}` : '' };
  }
  return null;
};

// 在 parse 前应用重写，并把新 URL 写回浏览器历史（replaceState，不污染回退）。
const parseUrlWithRewrite = (pathname, search) => {
  const next = rewriteLegacyUrl(pathname, search);
  if (next) {
    try {
      window.history.replaceState(null, '', next.pathname + next.search);
    } catch { /* SSR / sandbox 环境忽略 */ }
    return parseUrl(next.pathname, next.search);
  }
  return parseUrl(pathname, search);
};

const RouteProvider = ({ children }) => {
  const [route, setRoute] = useS(() => parseUrlWithRewrite(window.location.pathname, window.location.search));

  useE(() => {
    const onPop = () => setRoute(parseUrlWithRewrite(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Safety net for "无法下滑滚动": a modal / sheet / CmdPalette scroll-lock that didn't
  // clean up (overlapping locks capture each other's `prev` and restore in the wrong order,
  // leaving document.body stuck at overflow:hidden) makes the next page unscrollable.
  // Landing on a new route means no prior overlay should still hold the lock — so release it.
  useE(() => {
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
  }, [route.page, route.params]);

  const navigate = (page, params = {}) => {
    const url = buildUrl(page, params);
    const current = window.location.pathname + window.location.search;
    if (url !== current) window.history.pushState(null, '', url);
    setRoute({ page, params });
  };

  return <RouteCtx.Provider value={{ route, navigate }}>{children}</RouteCtx.Provider>;
};

// ---------- Auth-ish state (now backed by xapi-backend via window.XApi) ----------
const AuthCtx = createC(null);
const GUEST_USER = { handle: 'guest', accountType: 'VIRTUAL', balance: 0, earningsBalance: 0, canProvideApis: false, avatar: null, _raw: null };

const AuthProvider = ({ children }) => {
  const [user, setUser] = useS(GUEST_USER);
  const [authed, setAuthed] = useS(false);
  const [loading, setLoading] = useS(true);
  const sessionVersion = React.useRef(0);
  const logoutPromise = React.useRef(null);
  const profileRevision = React.useRef(0);
  const pendingProfile = React.useRef(null);
  const route = useCtx(RouteCtx)?.route;
  const previousRoute = React.useRef(route);
  const currentUser = React.useRef(user);
  currentUser.current = user;

  const refreshMe = React.useCallback(({ background = false } = {}) => {
    if (logoutPromise.current) return Promise.resolve(null);
    const version = sessionVersion.current;
    // Polls/focus bursts join the current read. Explicit post-write refreshes
    // start a new revision so an older /me can never overwrite their result.
    if (background && pendingProfile.current?.version === version &&
        pendingProfile.current.revision === profileRevision.current) return pendingProfile.current.promise;
    const revision = ++profileRevision.current;
    const isCurrent = () => sessionVersion.current === version && profileRevision.current === revision;
    const promise = (async () => {
      let t = authTokens.getAccess();
      if (!t) {
        try { t = await XApi.helpers.refreshAccessToken(); } catch { /* no valid cookie session */ }
      }
      if (!isCurrent()) return null;
      if (!t) { setUser(GUEST_USER); setAuthed(false); setLoading(false); return null; }
      const controller = new AbortController();
      let timer;
      try {
        const timeout = new Promise((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new DOMException('Profile refresh timed out', 'TimeoutError'));
          }, 10_000);
        });
        const u = await Promise.race([auth.getMe({ signal: controller.signal }), timeout]);
        if (!isCurrent()) return null;
        const adapted = auth.adaptUser(u);
        // Unchanged periodic profiles must not rerender every auth consumer
        // (or retrigger consumers that depend on the user object).
        setUser(previous => adapted && JSON.stringify(previous) === JSON.stringify(adapted)
          ? previous : (adapted || GUEST_USER));
        setAuthed(!!adapted);
        return adapted;
      } catch (e) {
        if (!isCurrent()) return null;
        // A temporary balance refresh failure must not log out a valid session.
        // Actual authentication failures still clear it (as does auth-cleared).
        if (e?.status === 401 || !currentUser.current?._raw?.id) {
          setUser(GUEST_USER); setAuthed(false);
        }
        return null;
      } finally { clearTimeout(timer); if (isCurrent()) setLoading(false); }
    })();
    pendingProfile.current = { version, revision, promise };
    void promise.finally(() => {
      if (pendingProfile.current?.promise === promise) pendingProfile.current = null;
    });
    return promise;
  }, []);

  const ownerVersion = sessionVersion.current;
  const ownerId = user?._raw?.id ?? null;
  const updateUser = React.useCallback((next) => {
    if (sessionVersion.current !== ownerVersion) return;
    // A payment receipt can update state before its confirming /me read.
    // Fence every already-started profile read before applying that update.
    profileRevision.current++;
    setUser(previous => {
      if (sessionVersion.current !== ownerVersion || (previous?._raw?.id ?? null) !== ownerId) return previous;
      return typeof next === 'function' ? next(previous) : next;
    });
  }, [ownerVersion, ownerId]);

  React.useEffect(() => {
    refreshMe();
    return () => { sessionVersion.current += 1; };
  }, [refreshMe]);

  React.useEffect(() => {
    if (!authed) return;
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') void refreshMe({ background: true });
    };
    const timer = setInterval(refreshVisible, 30_000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [authed, refreshMe]);

  React.useEffect(() => {
    const changed = previousRoute.current !== route;
    previousRoute.current = route;
    if (changed && authed && document.visibilityState === 'visible') void refreshMe({ background: true });
  }, [route, authed, refreshMe]);

  // If services.jsx fires "auth-cleared" (refresh failed), fall back to guest.
  React.useEffect(() => {
    const onCleared = () => {
      sessionVersion.current += 1;
      setUser(GUEST_USER); setAuthed(false); setLoading(false);
    };
    window.addEventListener('xapi:auth-cleared', onCleared);
    return () => window.removeEventListener('xapi:auth-cleared', onCleared);
  }, []);

  // Scope per-user client state by handle (preserves XEnv scoping behavior).
  React.useEffect(() => {
    window.__xapi_user_handle = user.handle;
    try { localStorage.setItem('xapi.currentUser', JSON.stringify({ handle: user.handle })); } catch {}
    try {
      const legacy = localStorage.getItem('xapi.envVars');
      const scoped = 'xapi.envVars:' + user.handle;
      if (legacy && !localStorage.getItem(scoped)) {
        localStorage.setItem(scoped, legacy);
        localStorage.removeItem('xapi.envVars');
      }
    } catch {}
    if (XEnv && XEnv._notify) XEnv._notify();
  }, [user.handle]);

  // ---- public methods for pages ----
  // Eagerly sync window.__xapi_user_handle BEFORE returning so callers (e.g. LoginPage that
  // immediately writes to XEnv) read the new handle, not the stale one. The `[user.handle]`
  // useEffect runs after render commits — too late for `await loginWithApiKey()` continuations.
  const adoptUser = (adapted) => {
    if (!adapted) return;
    sessionVersion.current += 1;
    window.__xapi_user_handle = adapted.handle;
    try { localStorage.setItem('xapi.currentUser', JSON.stringify({ handle: adapted.handle })); } catch {}
    if (XEnv && XEnv._notify) XEnv._notify();
    setUser(adapted); setAuthed(true);
  };

  const loginWithApiKey = async (apiKey) => {
    if (logoutPromise.current) await logoutPromise.current;
    const version = ++sessionVersion.current;
    setLoading(false);
    const isCurrent = () => sessionVersion.current === version;
    const assertCurrent = () => {
      if (!isCurrent()) throw new DOMException('Login was superseded', 'AbortError');
    };
    const out = await auth.loginWithApiKey(apiKey, { isCurrent });
    assertCurrent();
    let adapted = auth.adaptUser(out && out.user);
    if (!adapted) {
      const u = await auth.getMe().catch(() => null);
      assertCurrent();
      adapted = auth.adaptUser(u);
    }
    if (!adapted) {
      setUser(GUEST_USER); setAuthed(false);
      throw new Error('Login succeeded but user profile could not be loaded');
    }
    adoptUser(adapted);
    return adapted;
  };

  const quickStart = async ({ autoLogin = true, referralCode } = {}) => {
    const r = await auth.register(referralCode);
    if (autoLogin && r.apiKey) {
      const adapted = await loginWithApiKey(r.apiKey);
      return { ...r, user: adapted };
    }
    return r;
  };

  const logout = async () => {
    if (logoutPromise.current) return logoutPromise.current;
    // Invalidate outstanding /auth/me requests before waiting on the network.
    sessionVersion.current += 1;
    setUser(GUEST_USER); setAuthed(false); setLoading(false);
    const pending = auth.logout().catch(() => {});
    logoutPromise.current = pending;
    try { await pending; }
    finally { if (logoutPromise.current === pending) logoutPromise.current = null; }
  };

  // ---- Derived permission flags (mirrors xapi-new useAuth() shape) ----
  // Backend authoritatively gates with @RequireEntity / @ProviderWhitelistGuard / @RequireTwitterAuth;
  // these flags only drive UI affordances so the user isn't shown buttons that will 403.
  const raw = user && user._raw;
  const loginMethod = raw && raw.loginMethod;
  const isApiKeyLogin = loginMethod === 'apikey';
  // `isTwitterLogin` reflects the CURRENT session's auth method, not whether the account
  // has a Twitter handle linked. Backend's EntityAccountGuard rejects loginMethod==='apikey'
  // even for ENTITY accounts, so checking `raw.twitterId` here would mis-grant UI access
  // and surface raw 403s on /api-services/mine etc.
  const isTwitterLogin = loginMethod === 'twitter';
  const isEntity = user && user.accountType === 'ENTITY';
  // Whether the linked X handle is known (for badges / "Invited by" UX). Distinct from
  // isTwitterLogin which is about the current session's credentials.
  const hasLinkedTwitter = !!(raw && raw.twitterId);
  const hasFullPermissions = isEntity && isTwitterLogin;
  // Key CRUD requires ENTITY; PUT /keys/:id/limit additionally requires Twitter OAuth (per audit).
  const canManageKeys = isEntity;
  const canSetSpendLimit = hasFullPermissions;
  // This is only a presentation hint. The backend independently checks both the
  // account allowlist and the scope grant when XNT Cloud is used.
  const canUseXntCloud = hasFullPermissions && !!(raw && raw.xntCloudAllowed);
  // Provider access: trust backend's authoritative flag. Covers both paths:
  //   - ENTITY + admin whitelist → backend returns canProvideApis=true
  //   - apikey login with apikey.allowRegister=true → backend also returns true
  // Keeping the UI thin: any 403 from write-side endpoints is still handled gracefully.
  const canProvideApis = !!(raw && raw.canProvideApis);

  return (
    <AuthCtx.Provider value={{
      user, setUser: updateUser, authed, loading,
      // permission flags
      loginMethod, isApiKeyLogin, isTwitterLogin, isEntity, hasLinkedTwitter,
      hasFullPermissions, canManageKeys, canSetSpendLimit, canProvideApis,
      canUseXntCloud,
      // actions
      loginWithApiKey, quickStart, logout, refreshMe,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};
const useAuth = () => useCtx(AuthCtx);

// ---------- Top bar ----------
const TopBar = ({ onToggleCmd }) => {
  const { route, navigate } = useRoute();
  const { user, authed, logout, canProvideApis, refreshMe } = useAuth();
  const { t, locale, setLocale } = useT();
  const { IconSearch, IconBell, IconChevronDown, IconBlocks, IconPlus, IconMenu, IconX, IconWallet } = XIcons;
  const [profOpen, setProfOpen] = useS(false);
  const [emailOpen, setEmailOpen] = useS(false);
  // 当前用户作为「调用方」的全局客户代号(服务商看到的就是它,不暴露真实身份)。
  // 右上角 hover / 展开菜单可见。
  const [callerAlias, setCallerAlias] = useS(null);
  useE(() => {
    if (!authed) { setCallerAlias(null); return; }
    let cancelled = false;
    XApi.providerDashboardService.myCallerAlias()
      .then((a) => { if (!cancelled) setCallerAlias(a); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed]);
  // 「邀请赚返佣」徽标的比例——真实值来自 admin 可配置的用量返佣率(全局或该用户的
  // 特例覆盖)，未登录/请求失败时回退到当前全局默认 10%（与后端 DEFAULT_USAGE_RATE 一致）。
  const [inviteRate, setInviteRate] = useS(0.1);
  useE(() => {
    if (!authed) { setInviteRate(0.1); return; }
    let cancelled = false;
    XApi.referralService.info()
      .then((info) => { if (!cancelled && Number.isFinite(info?.usageRewardRate)) setInviteRate(info.usageRewardRate); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed]);
  const inviteRatePct = `${Math.round(inviteRate * 100)}%`;
  // Mobile sheet — closed by default. Opens via the hamburger in the topbar; closed
  // by tapping the backdrop, picking a nav link, or hitting the close button.
  const [sheetOpen, setSheetOpen] = useS(false);
  React.useEffect(() => {
    if (!profOpen) return;
    const close = () => setProfOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [profOpen]);
  // Lock body scroll while the mobile sheet is open so swipes don't bleed through to the page.
  React.useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);
  // Auto-close the sheet whenever the route changes (so navigation feels instant).
  React.useEffect(() => { setSheetOpen(false); }, [route.page, route.params]);

  const NavLink = ({ page, params, children }) => (
    <button
      onClick={() => navigate(page, params)}
      className="btn-ghost"
      style={{
        height: 32, padding: '0 10px', fontSize: 13,
        color: route.page === page && (!params?.tab || route.params?.tab === params.tab) ? 'var(--fg-0)' : 'var(--fg-2)',
        background: 'transparent',
        border: 'none',
        borderRadius: 5,
        cursor: 'pointer',
      }}>
      {children}
    </button>
  );

  const SheetLink = ({ page, params, children }) => (
    <button
      className={`ms-link ${route.page === page && (!params?.tab || route.params?.tab === params.tab) ? 'active' : ''}`}
      onClick={() => { setSheetOpen(false); navigate(page, params); }}
    >
      <span>{children}</span>
      <XIcons.IconChevronRight size={12} style={{ color: 'var(--fg-4)' }} />
    </button>
  );

  const LocaleToggle = () => (
    <div
      role="group"
      aria-label="Language"
      style={{
        display:'inline-flex', height: 32, padding: 2,
        background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius: 6,
        gap: 0,
      }}
    >
      {[
        { id: 'en', label: 'EN' },
        { id: 'zh', label: '中文' },
      ].map(opt => {
        const active = locale === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => { if (!active) setLocale(opt.id); }}
            aria-pressed={active}
            style={{
              height: 26, minWidth: 32, padding: '0 8px',
              border: 'none', borderRadius: 4,
              background: active ? 'var(--bg-3)' : 'transparent',
              color: active ? 'var(--fg-0)' : 'var(--fg-3)',
              fontFamily: opt.id === 'en' ? 'var(--font-mono)' : 'var(--font-sans, inherit)',
              fontSize: opt.id === 'en' ? 11 : 12,
              letterSpacing: opt.id === 'en' ? '0.06em' : 0,
              cursor: active ? 'default' : 'pointer',
              transition: 'background .15s ease, color .15s ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <header style={{
      height: 52,
      borderBottom: '1px solid var(--line)',
      background: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(8px)',
      position: 'sticky', top: 0, zIndex: 40,
      display: 'flex', alignItems: 'center',
      padding: '0 20px',
    }}>
      <button onClick={() => navigate('portal')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
        <XPrim.Logo />
      </button>
      <div className="topbar-desktop-only" style={{ width: 1, height: 20, background: 'var(--line-2)', margin: '0 16px' }} />
      <nav className="row gap-2 topbar-desktop-only">
        <NavLink page="market">{t('nav.discover')}</NavLink>
        <NavLink page="capability">{t('nav.capabilities')}</NavLink>
        <NavLink page="domains">{t('nav.domains')}</NavLink>
        <NavLink page="ai">{t('nav.ai')}</NavLink>
        {SHOW_SANDBOX_NAV && <NavLink page="playground">{t('nav.sandbox')}</NavLink>}
        <NavLink page="skill">{t('nav.skill')}</NavLink>
        {/* Skill Market 入口暂时隐藏 */}
        <NavLink page="mcp">{t('nav.mcp')}</NavLink>
        <NavLink page="activity">{t('nav.activity')}</NavLink>
        {/* Workspace — hidden for anon; shown for any authed user. Internal content
            branches on canProvideApis (full APIs table vs "Apply for provider access" CTA). */}
        {authed && <NavLink page="workspace">{t('nav.workspace')}</NavLink>}
      </nav>

      <div className="grow" />

      <button onClick={onToggleCmd} className="row gap-2 hairline topbar-desktop-only" style={{
        height: 32, padding: '0 10px 0 10px', borderRadius: 6, background: 'var(--bg-1)',
        color: 'var(--fg-3)', fontSize: 12, minWidth: 240,
      }}>
        <IconSearch size={13} />
        <span>{t('topbar.search')}</span>
        <div className="grow" />
        <span className="kbd">⌘</span><span className="kbd">K</span>
      </button>

      <div className="topbar-desktop-only" style={{ width: 10 }} />
      {/* Referral — prominent CTA (desktop only; available in mobile sheet too) */}
      <button onClick={() => navigate('console', { tab: 'referral' })}
        className="row gap-2 topbar-desktop-only"
        style={{
          height: 32, padding: '0 10px 0 10px',
          border: '1px solid rgba(245,165,36,0.4)',
          background: 'linear-gradient(180deg, rgba(245,165,36,0.14), rgba(245,165,36,0.06))',
          color: '#f5c87a', borderRadius: 6, fontSize: 12, cursor: 'pointer',
        }}>
        <XIcons.IconUsers size={13}/>
        <span style={{letterSpacing:'-0.005em'}}>{t('topbar.invite')}</span>
        <span className="mono" style={{color:'#f5a524', fontSize: 11, padding:'2px 6px', background:'rgba(245,165,36,0.15)', borderRadius: 3}}>{inviteRatePct}</span>
      </button>
      <div className="topbar-desktop-only" style={{ width: 8 }} />
      {/* Locale switch — desktop only. The mobile sheet exposes its own at the top. */}
      <span className="topbar-desktop-only"><LocaleToggle/></span>
      <div className="topbar-desktop-only" style={{ width: 8 }} />
      {/* Notifications bell hidden — feature not wired yet, the placeholder
          icon was misleading. Restore by uncommenting when notifications ship. */}
      {/* <button className="btn btn-sm btn-ghost btn-icon topbar-desktop-only" title="Notifications"><IconBell size={14}/></button>
      <div className="topbar-desktop-only" style={{ width: 8 }} /> */}

      {/* Auth chrome on desktop. Mobile collapses everything except a single primary action
          (Sign in for guests / balance pill for authed users), the rest moves into the sheet. */}
      <span className="topbar-desktop-only" style={{ display:'inline-flex', alignItems:'center', gap: 8 }}>
        {authed ? (
          <>
            {/* 一个余额块，两段：左=充值余额(可消费)，右=可提现收益(API 收入，mint 色区分)。 */}
            <div className="row" style={{ height: 32, border: '1px solid var(--line-2)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-1)' }}>
              <button onClick={() => navigate('console', { tab: 'billing', payment: 'manual' })} title={t('topbar.rechargeBalance')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', height: '100%', border: 'none', background: 'transparent', color: 'var(--fg-1)', cursor: 'pointer' }}>
                <span className="mono" style={{ fontSize: 12 }}>${user.balance.toFixed(2)}</span>
                <IconPlus size={11} style={{ opacity: 0.55 }} />
              </button>
              {(user.canProvideApis || (user.earningsBalance || 0) > 0) && (
                <>
                  <span style={{ width: 1, height: '100%', background: 'var(--line)' }} />
                  <button onClick={() => navigate('earnings')} title={t('topbar.withdrawableEarnings')}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 10px', height: '100%', border: 'none', background: 'rgba(95,208,168,0.07)', cursor: 'pointer' }}>
                    <IconWallet size={11} style={{ color: 'var(--c-mint, #5fd0a8)' }} />
                    <span className="mono mint-text" style={{ fontSize: 12 }}>${(user.earningsBalance || 0).toFixed(2)}</span>
                  </button>
                </>
              )}
            </div>
            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setProfOpen(o => !o)}
                className="row gap-2"
                title={callerAlias?.callerHandle ? `你的客户代号:${callerAlias.callerHandle} —— 服务商看到的就是它,不暴露真实身份` : undefined}
                style={{
                  height: 32, padding: '0 4px 0 10px', border: '1px solid var(--line-2)', borderRadius: 6,
                  background: 'var(--bg-1)', cursor: 'pointer',
                }}
              >
                <span style={{fontSize:12}}>{user.handle}</span>
                <div style={{
                  width: 22, height: 22, borderRadius: 4,
                  background: 'linear-gradient(135deg,#2a2a2a,#111)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'var(--font-mono)', fontSize: 11, color:'var(--fg-1)',
                }}>{(user.handle||'?')[0].toLowerCase()}</div>
              </button>
              {profOpen && (
                <div className="card" style={{
                  position:'absolute', right:0, top: 38, minWidth: 180, padding: 6, zIndex: 60,
                  boxShadow:'0 12px 32px rgba(0,0,0,0.5)',
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                  <div style={{ padding: '4px 8px 6px', borderBottom: '1px solid var(--line)', marginBottom: 2 }}
                       title="服务商看到你的就是这个代号,不暴露真实身份">
                    <div style={{ fontSize: 10, color: 'var(--fg-4)' }}>你的客户代号</div>
                    <div className="mono" style={{ fontSize: 12, color: 'var(--fg-1)' }}>{callerAlias?.callerHandle || '—'}</div>
                  </div>
                  <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'flex-start'}} onClick={() => { setProfOpen(false); navigate('console'); }}>{t('topbar.console')}</button>
                  <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'flex-start'}} onClick={() => { setProfOpen(false); navigate('console', {tab:'keys'}); }}>{t('topbar.apiKeys')}</button>
                  <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'flex-start'}} onClick={() => { setProfOpen(false); navigate('my-apis'); }}>{t('topbar.myApis')}</button>
                  {(user.canProvideApis || (user.earningsBalance || 0) > 0) && (
                    <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'space-between', alignItems:'center', gap: 8}} onClick={() => { setProfOpen(false); navigate('earnings'); }}>
                      <span>{t('topbar.earnings')}</span>
                      <span className="mono mint-text" style={{ fontSize: 11 }}>${(user.earningsBalance || 0).toFixed(2)}</span>
                    </button>
                  )}
                  <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'flex-start', alignItems:'center', gap: 6}} onClick={() => { setProfOpen(false); setEmailOpen(true); }}>
                    <span>{user.email ? t('topbar.emailSettings') : t('topbar.bindEmail')}</span>
                    {user.email
                      ? <span className="tag-dot ok" style={{marginLeft:'auto'}} title={user.email}/>
                      : <span className="tag-dot" style={{marginLeft:'auto', background:'var(--warn)'}} title={t('topbar.emailUnbound')}/>}
                  </button>
                  <div style={{height:1, background:'var(--line)', margin:'4px 0'}}/>
                  <button className="btn btn-ghost" style={{display:'flex', width:'100%', justifyContent:'flex-start', color:'var(--err)'}} onClick={async () => { setProfOpen(false); await logout(); navigate('portal'); }}>{t('auth.signOut')}</button>
                </div>
              )}
            </div>
          </>
        ) : (
          <button onClick={() => navigate('login')} className="btn btn-sm btn-primary">{t('auth.getStarted')}</button>
        )}
      </span>

      {/* Mobile chrome — single primary auth pill + hamburger */}
      <span className="topbar-mobile-only" style={{ alignItems:'center', gap: 8 }}>
        {authed ? (
          <button onClick={() => navigate('console', { tab: 'billing', payment: 'manual' })} className="btn btn-sm">
            <span className="mono">${user.balance.toFixed(2)}</span>
          </button>
        ) : (
          <button onClick={() => navigate('login')} className="btn btn-sm btn-primary">{t('auth.getStarted')}</button>
        )}
        <button
          onClick={() => setSheetOpen(true)}
          aria-label={t('nav.menu')}
          className="btn btn-sm btn-ghost btn-icon"
          style={{ marginLeft: 4 }}
        >
          <IconMenu size={16}/>
        </button>
      </span>

      {/* Mobile sheet — rendered via portal at document.body so it escapes the
          header's stacking context. The header has `backdrop-filter: blur(8px)`
          which makes it a containing block for `position: fixed` descendants;
          rendering inside the header would clip the sheet to the 52px header
          height. Portal puts it on body where viewport-fixed positioning works. */}
      {sheetOpen && typeof document !== 'undefined' && createPortal(
        <>
          <div className="mobile-sheet-backdrop" onClick={() => setSheetOpen(false)} />
          <aside className="mobile-sheet" role="dialog" aria-label={t('nav.menu')} onClick={(e) => e.stopPropagation()}>
            <div className="row between" style={{ alignItems:'center', padding:'4px 8px 12px' }}>
              <LocaleToggle />
              <button
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
                className="btn btn-sm btn-ghost btn-icon"
              >
                <IconX size={14}/>
              </button>
            </div>
            <div className="ms-section">{t('nav.menu')}</div>
            <SheetLink page="market">{t('nav.discover')}</SheetLink>
            <SheetLink page="capability">{t('nav.capabilities')}</SheetLink>
            <SheetLink page="domains">{t('nav.domains')}</SheetLink>
            <SheetLink page="ai">{t('nav.ai')}</SheetLink>
            {SHOW_SANDBOX_NAV && <SheetLink page="playground">{t('nav.sandbox')}</SheetLink>}
            <SheetLink page="skill">{t('nav.skill')}</SheetLink>
            {/* Skill Market 入口暂时隐藏 */}
            <SheetLink page="mcp">{t('nav.mcp')}</SheetLink>
            <SheetLink page="activity">{t('nav.activity')}</SheetLink>
            {authed && <SheetLink page="workspace">{t('nav.workspace')}</SheetLink>}

            <div className="ms-divider" />
            <button
              className="ms-link"
              onClick={() => { setSheetOpen(false); navigate('console', { tab: 'referral' }); }}
              style={{ color:'#f5c87a' }}
            >
              <span>{t('topbar.invite')}</span>
              <span className="mono" style={{color:'#f5a524', fontSize: 11, padding:'2px 6px', background:'rgba(245,165,36,0.15)', borderRadius: 3}}>{inviteRatePct}</span>
            </button>

            {authed && (
              <>
                <div className="ms-divider" />
                <div className="ms-section">{t('nav.account')}</div>
                <SheetLink page="console">{t('topbar.console')}</SheetLink>
                <button
                  className="ms-link"
                  onClick={() => { setSheetOpen(false); navigate('console', { tab: 'keys' }); }}
                ><span>{t('topbar.apiKeys')}</span><XIcons.IconChevronRight size={12} style={{ color: 'var(--fg-4)' }}/></button>
                <button
                  className="ms-link"
                  onClick={() => { setSheetOpen(false); navigate('my-apis'); }}
                ><span>{t('topbar.myApis')}</span><XIcons.IconChevronRight size={12} style={{ color: 'var(--fg-4)' }}/></button>
                <button
                  className="ms-link"
                  onClick={() => { setSheetOpen(false); setEmailOpen(true); }}
                >
                  <span>{user.email ? t('topbar.emailSettings') : t('topbar.bindEmail')}</span>
                  {user.email
                    ? <span className="tag-dot ok" title={user.email}/>
                    : <span className="tag-dot" style={{ background:'var(--warn)' }} title={t('topbar.emailUnbound')}/>}
                </button>
                <button
                  className="ms-link"
                  onClick={async () => { setSheetOpen(false); await logout(); navigate('portal'); }}
                  style={{ color:'var(--err)' }}
                ><span>{t('auth.signOut')}</span></button>
              </>
            )}
          </aside>
        </>,
        document.body,
      )}

      {/* Email-bind dialog — single mount shared by the desktop account menu and
          the mobile sheet. Dialog renders via its own portal, so placing it at
          the header root (outside the desktop/mobile-only spans) is fine. */}
      {emailOpen && (
        <EmailVerifyModal
          isChange={!!user.email}
          initialEmail={user.email || ''}
          onClose={() => setEmailOpen(false)}
          onDone={async () => { setEmailOpen(false); try { await refreshMe(); } catch {} }}
        />
      )}
    </header>
  );
};

// ---------- Command palette (⌘K) ----------
// Mirrors the discover page's search semantics: search the public API catalog,
// ranked by 30-day call count (server-side sort in findAll). Each row shows the
// same fields a marketplace card shows — logo / name / description / last
// update / 30-day hits — so ⌘K stays a fast keyboard route to the same answer
// the user would get clicking through to /discover.
const fmtRelTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 30)return `${Math.floor(diff / (86400 * 7))}w ago`;
  return `${Math.floor(diff / (86400 * 30))}mo ago`;
};
const fmtNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(v);
};

const CmdPalette = ({ open, onClose }) => {
  const { navigate } = useRoute();
  const [q, setQ] = useS('');
  // Backend already sorts by 30-day hits desc + injects totalHits per row
  // (api.service.ts findAll).
  // Refetch on every open (not cached across sessions) so DB swaps or
  // newly-registered APIs show up immediately without needing a page
  // reload. The payload is ~10KB, refetch cost is negligible.
  // `loaded` distinguishes "fetch in flight" from "fetch done, got 0
  // results" — otherwise typing a query mid-fetch wrongly shows
  // "没有匹配的 API" instead of "加载中…".
  const [apis, setApis] = useS([]);
  const [loaded, setLoaded] = useS(false);
  // Keyboard-active row for ↑/↓ + Enter navigation. Mouse hover adopts the
  // same index (Raycast-style) so pointer and keyboard never fight over
  // which row is "current". Reset whenever the query (and thus the list) changes.
  const [active, setActive] = useS(0);
  useE(() => { if (open) { setQ(''); setActive(0); } }, [open]);
  useE(() => {
    if (!open) return;
    setLoaded(false);
    let cancelled = false;
    XApi.apiSvc.listMarketplace()
      .then(list => {
        if (cancelled) return;
        setApis(Array.isArray(list) ? list : []);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [open]);
  if (!open) return null;

  // Match on name + description (same fields the discover-page input filters).
  const needle = q.trim().toLowerCase();
  const filtered = !needle
    ? apis
    : apis.filter(a =>
        ((a.name || '') + ' ' + (a.description || '')).toLowerCase().includes(needle),
      );
  const shown = filtered.slice(0, 20);
  const clampedActive = Math.min(active, Math.max(0, shown.length - 1));
  const openItem = (a) => { navigate('api-detail', { id: XApi.helpers.apiSlug(a) }); onClose(); };
  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!shown.length) return;
      const next = e.key === 'ArrowDown'
        ? (clampedActive + 1) % shown.length
        : (clampedActive - 1 + shown.length) % shown.length;
      setActive(next);
      // Keep the active row visible without yanking the scroll position.
      requestAnimationFrame(() => {
        document.querySelector(`.cmd-row[data-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
      });
    } else if (e.key === 'Enter' && shown[clampedActive]) {
      e.preventDefault();
      openItem(shown[clampedActive]);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div style={{
        width: 600, maxWidth: 'calc(100% - 48px)', background: 'var(--bg-1)',
        border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', padding:'12px 14px', borderBottom:'1px solid var(--line)'}}>
          <XIcons.IconSearch size={14} />
          <input
            autoFocus
            placeholder="搜索 API…"
            value={q} onChange={e => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onSearchKeyDown}
            style={{ flex: 1, marginLeft: 10, background:'transparent', border:'none', outline:'none', fontSize: 14, color:'var(--fg-0)' }}
          />
          <span className="kbd">esc</span>
        </div>
        <div style={{ maxHeight: 520, overflow: 'auto' }}>
          {shown.map((a, i) => {
            const initial = (a.name || '?').trim().charAt(0).toUpperCase();
            return (
              <div key={a.id || i}
                   data-idx={i}
                   onClick={() => openItem(a)}
                   className={`cmd-row row gap-3${i === clampedActive ? ' is-active' : ''}`}
                   style={{
                     padding: '12px 14px', cursor:'pointer',
                     borderBottom: i < shown.length - 1 ? '1px solid var(--line)' : 'none',
                     alignItems: 'center',
                   }}
                   onMouseEnter={() => setActive(i)}>
                {/* Avatar — logoUrl if available, else first-letter mono badge */}
                {a.logoUrl ? (
                  <img src={a.logoUrl} alt=""
                       style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0, background: 'var(--bg-2)' }}
                       onError={e => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div style={{
                    width: 36, height: 36, borderRadius: 6, background: 'var(--bg-3)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize: 14, fontWeight: 600, color: 'var(--fg-2)', flexShrink: 0,
                  }}>{initial}</div>
                )}

                <div className="grow" style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.name}
                  </div>
                  <div className="clamp-2" style={{ fontSize: 11.5, color: 'var(--fg-4)', marginTop: 2, lineHeight: 1.35 }}>
                    {a.description || '—'}
                  </div>
                </div>

                <div style={{ flexShrink: 0, textAlign: 'right', fontSize: 11 }}>
                  <div className="mono" style={{ color: 'var(--fg-2)' }}>
                    {fmtNum(a.totalHits)} <span style={{ color: 'var(--fg-4)' }}>calls</span>
                  </div>
                  <div className="mono" style={{ color: 'var(--fg-4)', marginTop: 2 }}>
                    {fmtRelTime(a.updatedAt || a.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
          {!filtered.length && (
            <div style={{ padding: 40, textAlign:'center', color:'var(--fg-4)', fontSize: 13 }}>
              {!loaded ? '加载中…' : '没有匹配的 API'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const XShell = { RouteCtx, RouteProvider, useRoute, AuthProvider, useAuth, TopBar, CmdPalette };
export { RouteCtx, RouteProvider, useRoute, AuthProvider, useAuth, TopBar, CmdPalette };
if (typeof window !== 'undefined') window.XShell = XShell;
